<template>
  <!-- This component has no UI, it just handles connection reset events -->
  <div style="display: none;"></div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted } from 'vue';
import { logger, LogCategory } from '@/services/logger';
import { supabase } from '@/services/supabase';

// eslint-disable-next-line @typescript-eslint/no-unused-vars

export default defineComponent({
  name: 'ConnectionResetHandler',
  setup() {
    // Reference to event handlers
    let reloadPageHandler: ((event: CustomEvent) => void) | null = null;
    let superAggressiveReconnectHandler: ((event: CustomEvent) => void) | null = null;
    
    // Handle the reload-page event
    const handleReloadPage = (event: CustomEvent) => {
      console.log('[CONNECTION RESET] Reload page event received:', event.detail);
      logger.info('Reload page event received', LogCategory.DATABASE, { detail: event.detail });
      
      // Show a message to the user
      const message = 'Connection reset required. Attempting aggressive reconnection...';
      console.log('[CONNECTION RESET] ' + message);
      
      // Instead of reloading the page, try an aggressive reconnection strategy
      setTimeout(async () => {
        try {
          console.log('[CONNECTION RESET] Performing aggressive reconnection...');
          
          // 1. Force a session refresh
          const { data: sessionData, error: sessionError } = await supabase.auth.refreshSession();
          if (sessionError) {
            console.error('[CONNECTION RESET] Error refreshing session:', sessionError);
          } else if (sessionData.session) {
            console.log('[CONNECTION RESET] Session refreshed successfully');
          }
          
          // 2. Force a reconnection of all realtime channels
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const channels = (supabase as any).getChannels ? (supabase as any).getChannels() : [];
          console.log('[CONNECTION RESET] Found', channels.length, 'realtime channels');
          
          for (const channel of channels) {
            try {
              console.log('[CONNECTION RESET] Reconnecting channel:', channel.topic);
              channel.unsubscribe();
              setTimeout(() => {
                try {
                  channel.subscribe();
                  console.log('[CONNECTION RESET] Successfully reconnected channel:', channel.topic);
                } catch (subErr) {
                  console.error('[CONNECTION RESET] Error resubscribing to channel:', channel.topic, subErr);
                }
              }, 100);
            } catch (channelErr) {
              console.error('[CONNECTION RESET] Error reconnecting channel:', channel.topic, channelErr);
            }
          }
          
          // 3. Dispatch events to notify components
          console.log('[CONNECTION RESET] Dispatching reconnection events');
          
          // Dispatch force-refresh event
          const forceRefreshEvent = new CustomEvent('force-refresh', {
            detail: {
              timestamp: new Date().toISOString(),
              source: 'connection-reset-handler'
            }
          });
          // eslint-disable-next-line no-undef
          window.dispatchEvent(forceRefreshEvent as unknown as Event);
          
          // Dispatch force-auth-refresh event
          const forceAuthRefreshEvent = new CustomEvent('force-auth-refresh', {
            detail: {
              timestamp: new Date().toISOString(),
              source: 'connection-reset-handler'
            }
          });
          // eslint-disable-next-line no-undef
          window.dispatchEvent(forceAuthRefreshEvent as unknown as Event);
          
          console.log('[CONNECTION RESET] Aggressive reconnection completed');
        } catch (err) {
          console.error('[CONNECTION RESET] Error during aggressive reconnection:', err);
        }
      }, 500);
    };
    
    // Handle the super-aggressive-reconnect event
    const handleSuperAggressiveReconnect = async (event: CustomEvent) => {
      console.log('[CONNECTION RESET] Super aggressive reconnect event received:', event.detail);
      logger.info('Super aggressive reconnect event received', LogCategory.DATABASE, { detail: event.detail });
      
      // Show a message to the user
      const message = 'Connection issues detected. Performing super aggressive reconnection...';
      console.log('[CONNECTION RESET] ' + message);
      
      try {
        console.log('[CONNECTION RESET] Starting super aggressive reconnection sequence');
        
        // 1. Force disconnect and reconnect of realtime
        try {
          console.log('[CONNECTION RESET] Forcing realtime disconnect/reconnect');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const realtime = (supabase as any).realtime;
          if (realtime) {
            if (typeof realtime.disconnect === 'function') {
              await realtime.disconnect();
              console.log('[CONNECTION RESET] Realtime disconnected');
            }
            
            // Small delay before reconnecting
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (typeof realtime.connect === 'function') {
              await realtime.connect();
              console.log('[CONNECTION RESET] Realtime reconnected');
            }
          }
        } catch (realtimeErr) {
          console.error('[CONNECTION RESET] Error with realtime reconnection:', realtimeErr);
        }
        
        // 2. Force a complete session refresh
        try {
          console.log('[CONNECTION RESET] Forcing complete session refresh');
          
          // Get current session
          const { data: sessionData } = await supabase.auth.getSession();
          
          if (sessionData.session) {
            // Extract tokens
            const { access_token, refresh_token } = sessionData.session;
            
            // Force refresh
            await supabase.auth.refreshSession();
            
            // Force set session
            await supabase.auth.setSession({
              access_token,
              refresh_token
            });
            
            console.log('[CONNECTION RESET] Session completely refreshed');
          } else {
            console.warn('[CONNECTION RESET] No session found during super aggressive reconnect');
          }
        } catch (sessionErr) {
          console.error('[CONNECTION RESET] Error with session refresh:', sessionErr);
        }
        
        // 3. Force reconnection of all channels
        try {
          console.log('[CONNECTION RESET] Forcing reconnection of all channels');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const channels = (supabase as any).getChannels ? (supabase as any).getChannels() : [];
          console.log('[CONNECTION RESET] Found', channels.length, 'channels to reconnect');
          
          for (const channel of channels) {
            try {
              console.log('[CONNECTION RESET] Reconnecting channel:', channel.topic);
              
              // Unsubscribe
              channel.unsubscribe();
              
              // Small delay before resubscribing
              await new Promise(resolve => setTimeout(resolve, 50));
              
              // Subscribe again
              channel.subscribe();
              console.log('[CONNECTION RESET] Channel reconnected:', channel.topic);
            } catch (channelErr) {
              console.error('[CONNECTION RESET] Error reconnecting channel:', channel.topic, channelErr);
            }
          }
        } catch (channelsErr) {
          console.error('[CONNECTION RESET] Error reconnecting channels:', channelsErr);
        }
        
        // 4. Force a database ping to ensure connection
        try {
          console.log('[CONNECTION RESET] Forcing database ping');
          const { error: pingError } = await supabase.from('profiles').select('count').limit(1);
          
          if (pingError) {
            console.error('[CONNECTION RESET] Database ping failed:', pingError);
          } else {
            console.log('[CONNECTION RESET] Database ping successful');
          }
        } catch (pingErr) {
          console.error('[CONNECTION RESET] Error with database ping:', pingErr);
        }
        
        // 5. Dispatch events to notify components
        console.log('[CONNECTION RESET] Dispatching notification events');
        
        // Dispatch events with small delays between them
        const events = [
          { name: 'force-refresh', source: 'super-aggressive-reconnect' },
          { name: 'force-auth-refresh', source: 'super-aggressive-reconnect' },
          { name: 'database-reconnected', source: 'super-aggressive-reconnect' },
          { name: 'supabase-reconnected', source: 'super-aggressive-reconnect' },
          { name: 'supabase-complete-reset', source: 'super-aggressive-reconnect' }
        ];
        
        for (const eventInfo of events) {
          await new Promise(resolve => setTimeout(resolve, 50));
          
          const customEvent = new CustomEvent(eventInfo.name, {
            detail: {
              timestamp: new Date().toISOString(),
              source: eventInfo.source
            }
          });
          // eslint-disable-next-line no-undef
          window.dispatchEvent(customEvent as unknown as Event);
          console.log(`[CONNECTION RESET] Dispatched ${eventInfo.name} event`);
        }
        
        console.log('[CONNECTION RESET] Super aggressive reconnection sequence completed');
      } catch (err) {
        console.error('[CONNECTION RESET] Error during super aggressive reconnection:', err);
      }
    };
    
    // Set up event listeners when the component is mounted
    onMounted(() => {
      console.log('[CONNECTION RESET] Component mounted, setting up event listeners');
      
      // Set up the reload-page event handler
      reloadPageHandler = handleReloadPage;
      // eslint-disable-next-line no-undef
      window.addEventListener('reload-page', reloadPageHandler as unknown as EventListener);
      
      // Set up the super-aggressive-reconnect event handler
      superAggressiveReconnectHandler = handleSuperAggressiveReconnect;
      // eslint-disable-next-line no-undef
      window.addEventListener('super-aggressive-reconnect', superAggressiveReconnectHandler as unknown as EventListener);
    });
    
    // Clean up event listeners when the component is unmounted
    onUnmounted(() => {
      console.log('[CONNECTION RESET] Component unmounted, cleaning up event listeners');
      
      // Remove the reload-page event handler
      if (reloadPageHandler) {
        // eslint-disable-next-line no-undef
        window.removeEventListener('reload-page', reloadPageHandler as unknown as EventListener);
        reloadPageHandler = null;
      }
      
      // Remove the super-aggressive-reconnect event handler
      if (superAggressiveReconnectHandler) {
        // eslint-disable-next-line no-undef
        window.removeEventListener('super-aggressive-reconnect', superAggressiveReconnectHandler as unknown as EventListener);
        superAggressiveReconnectHandler = null;
      }
    });
    
    return {};
  }
});
</script>